import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import { ReservationCustomer } from '../models/reservationCustomer';
import { ReservationEmailType } from '../models/reservation';
import { Upload } from '../models/upload';

export class ReservationCustomerService extends BaseService<ReservationCustomer> {
  override model = ReservationCustomer;

  override modelName = 'reservationCustomer';
  override modelLabel = 'Reservation Customers';

  async listIDs(ids: (string | number)[]): Promise<ReservationCustomer[]> {
    const allItems: ReservationCustomer[] = [];
    const seenIds = new Set<string>();
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const request = new ApiRequest({
        method: 'POST',
        url: `${this.modelBaseUrl}/ids`,
        data: { ids },
        query: { page: page.toString() },
      });

      const response = await this.requestList<ReservationCustomer>(request, ReservationCustomer);

      const items = response.items || [];
      items.forEach((item) => {
        const itemId = item.objectID();

        if (!itemId) {
          allItems.push(item);
          return;
        }

        if (seenIds.has(itemId)) { return; }

        seenIds.add(itemId);
        allItems.push(item);
      });

      hasMore = response.paging
        ? page < response.paging.pageCount
        : items.length >= 20;
      page++;
    }

    return allItems;
  }

  async cancel(
    reservationCustomerId: string,
    date: number,
    {
      sendEmail = false,
      future = false,
      cancellationTypeId,
      cancellationNote,
    }: {
      sendEmail?: boolean;
      future?: boolean;
      cancellationTypeId?: number;
      cancellationNote?: string;
    } = {}
  ): Promise<ApiResponse<ReservationCustomer>> {
    const query: Record<string, string> = {
      date: date.toString(),
      sendEmail: sendEmail ? '1' : '0',
      future: future ? '1' : '0',
    };

    if (cancellationTypeId != null) query.type = cancellationTypeId.toString();
    if (cancellationNote) query.note = cancellationNote;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/cancel`,
      query,
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async unCancel(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/cancel`,
      query: { sendEmail: '0', future: '0', undo: '1' },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async checkIn(
    reservationCustomerId: string,
    { findPass = false, customerPassId }: { findPass?: boolean; customerPassId?: string } = {}
  ): Promise<ApiResponse<ReservationCustomer>> {
    const query: Record<string, string> = {
      findPass: findPass ? '1' : '0',
    };
    if (customerPassId) query.customerPass = customerPassId;

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/checkIn`,
      query,
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async unCheckIn(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/checkIn`,
      query: { findPass: '0', customerPass: '', undo: '1' },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async didNotShow(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/noShow`,
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async unDidNotShow(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/noShow`,
      query: { undo: '1' },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async deleteReservationCustomer(
    reservationCustomerId: string,
    date: number,
    { future = false }: { future?: boolean } = {}
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/delete`,
      query: { date: date.toString(), future: future ? '1' : '0' },
    });

    return await this.request(request);
  }

  async unDelete(reservationCustomerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/delete`,
      query: { undo: '1' },
    });

    return await this.request(request);
  }

  async restoreReservationCustomer(reservationCustomerId: string): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/restore`,
    });

    return await this.request(request);
  }

  async confirm(
    reservationCustomerId: string,
    { sendEmail = false, undo = false }: { sendEmail?: boolean; undo?: boolean } = {}
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/confirm`,
      query: { sendEmail: sendEmail ? '1' : '0', undo: undo ? '1' : '0' },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async email(
    reservationCustomerId: string,
    date: number,
    type: ReservationEmailType = ReservationEmailType.received
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/email`,
      query: { date: date.toString(), type },
    });

    return await this.request(request);
  }

  async uploads(
    reservationCustomerId: string,
    { page = 1 }: { page?: number } = {}
  ): Promise<ApiListResponse<Upload>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/uploads`,
      query: { page: page.toString() },
    });

    return await this.requestList<Upload>(request, Upload);
  }

  async applyVoucher(
    reservationCustomerId: string,
    voucherId: string
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/applyVoucher`,
      query: { voucher: voucherId },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async removeVoucher(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/removeVoucher`,
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async assignCustomerPass(
    reservationCustomerId: string,
    customerPassId: string
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/assignCustomerPass`,
      query: { pass: customerPassId },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async removeCustomerPass(reservationCustomerId: string): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/removeCustomerPass`,
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async assignCustomerPasses(
    reservationCustomerId: string,
    customerPasses: any
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/assignCustomerPass`,
      data: { customerPasses },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async applyVouchers(
    reservationCustomerId: string,
    vouchers: any
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/applyVoucher`,
      data: { vouchers },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }

  async integrationAction(
    reservationCustomerId: string,
    { guid, actionCode }: { guid: string; actionCode: string }
  ): Promise<ApiResponse<ReservationCustomer>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${reservationCustomerId}/integrationAction`,
      query: { guid, action: actionCode },
    });

    return await this.requestItem<ReservationCustomer>(request, ReservationCustomer);
  }
}
